import { inject, Injectable } from '@angular/core';
import { Router } from '@angular/router';
import { AddShiftResponse } from '@api/src/models/AgentApi/Contract/add-shift-response';
import { BalanceResponse } from '@api/src/models/AgentApi/Contract/balance-response';
import { GetShiftReportRequest } from '@api/src/models/AgentApi/Contract/get-shift-report-request';
import { GetShiftReportResponse } from '@api/src/models/AgentApi/Contract/get-shift-report-response';
import { ListIdtypeXResponse } from '@api/src/models/AgentApi/Contract/list-idtype-x-response';
import { SellerSettings } from '@api/src/models/AgentApi/Contract/seller-settings';
import { SetShiftCloseRequest } from '@api/src/models/AgentApi/Contract/set-shift-close-request';
import { LoginRequest } from '@api/src/models/UserService/Contract/login-request';
import { LoginResponse } from '@api/src/models/UserService/Contract/login-response';
import { AgentService } from '@api/src/services';
import { LoginService } from '@api/src/services/login.service';
import { startWithErrors } from '@core/operators/start-with-errors';
import { startWithLoading } from '@core/operators/start-with-loading';
import { RouteEntity } from '@core/router/route.entity';
import { StorageKeys } from '@core/storage/storage.keys';
import { RxState, rxState } from '@rx-angular/state';
import { rxActions } from '@rx-angular/state/actions';
import { ClientState } from '@shared/state/client.state';
import { LocalStorageService } from 'ngx-webstorage';
import { EMPTY, Observable } from 'rxjs';
import { catchError, filter, tap } from 'rxjs/operators';
import { ErrorResponse } from '../error/error-response.model';
import { StartWithUnit } from '../operators/start-with-unit';

export type IUserState = {
    loading: Record<StartWithUnit, boolean>;
    errors: Record<StartWithUnit, ErrorResponse>;
    isAuthorized: boolean;
    user: LoginResponse;
    shift: AddShiftResponse;
    shiftReport: GetShiftReportResponse;
    mediumTypes: ListIdtypeXResponse[];
    balance: BalanceResponse;
    sellerSettings: SellerSettings;
    termCode: string;
};

export type IUserActions = {
    setIsAuthorizesFlag: boolean;
    requestMe: LoginResponse;
    login: LoginRequest;
    requestBalance: BalanceResponse;
    requestShift: AddShiftResponse;
    requestShiftReport: GetShiftReportResponse;
    requestMediumTypes: ListIdtypeXResponse[];
    requestSellerSettings: SellerSettings;
};

@Injectable({
    providedIn: 'root',
})
export class UserState extends RxState<IUserState> {
    readonly #loginService: LoginService = inject(LoginService);
    readonly #agentService: AgentService = inject(AgentService);
    readonly #localStorageService: LocalStorageService = inject(LocalStorageService);
    readonly #clientState: ClientState = inject(ClientState);
    readonly #router: Router = inject(Router);
    readonly #actions = rxActions<IUserActions>();

    readonly #state = rxState<IUserState>();

    public readonly isAuthorized$: Observable<boolean> = this.#state.select('isAuthorized');
    public readonly user$: Observable<LoginResponse> = this.#state.select('user');
    public readonly shift$: Observable<AddShiftResponse> = this.#state.select('shift');
    public readonly mediumTypes$: Observable<ListIdtypeXResponse[]> = this.#state.select('mediumTypes');
    public readonly loading$: Observable<Record<StartWithUnit, boolean>> = this.#state.select('loading');
    public readonly errors$: Observable<Record<StartWithUnit, ErrorResponse>> = this.#state.select('errors');
    public readonly balance$: Observable<BalanceResponse> = this.#state.select('balance');
    public readonly shiftReport$: Observable<GetShiftReportResponse> = this.#state.select('shiftReport');
    public readonly sellerSettings$: Observable<SellerSettings> = this.#state.select('sellerSettings');
    public readonly termCode$: Observable<string> = this.#state.select('termCode');

    constructor() {
        super();

        this.setDefaultState();
        this.connectSelectors();
    }

    public get authToken(): string {
        return this.#localStorageService.retrieve(StorageKeys.Access) || null;
    }

    public get termCode(): string {
        return this.#localStorageService.retrieve(StorageKeys.TermCode) || null;
    }

    public signIn(body: LoginRequest): void {
        this.hold(
            this.#loginService
                .apiLoginCheckLoginPost({ body })
                .pipe(
                    startWithLoading(this.#state, StartWithUnit.SignIn),
                    startWithErrors(this.#state, StartWithUnit.SignIn),
                ),
            (loginData: LoginResponse) => {
                this.#state.set({
                    errors: null,
                });
                this.#localStorageService.store(StorageKeys.Access, loginData.usrId);
                this.requestMe();
            },
        );
    }

    public requestShift(): void {
        this.hold(
            this.#agentService
                .apiAgentAddShiftPost({
                    body: {
                        termCode: this.termCode,
                    },
                })
                .pipe(
                    startWithLoading(this.#state, StartWithUnit.OpenShift),
                    startWithErrors(this.#state, StartWithUnit.OpenShift),
                ),
            this.#actions.requestShift,
        );
    }

    public closeShift(body: SetShiftCloseRequest): void {
        this.hold(this.#agentService.apiAgentSetShiftClosePost({ body }), () => {
            this.requestShiftReport({ shiftId: body.shiftId });
            this.#actions.requestShift(null);
        });
    }

    public requestMe(): void {
        this.hold(
            this.#loginService.apiLoginGetUserDataGet().pipe(
                catchError(() => {
                    this.#actions.setIsAuthorizesFlag(false);

                    return EMPTY;
                }),
            ),
            this.#actions.requestMe,
        );
    }

    public logout(): void {
        this.hold(
            this.#loginService.apiLoginLogoutGet().pipe(
                catchError(() => {
                    this.#actions.setIsAuthorizesFlag(false);

                    return EMPTY;
                }),
            ),
            () => {
                this.#localStorageService.clear(StorageKeys.Access);
                this.#actions.setIsAuthorizesFlag(false);
            },
        );
    }

    public requestMediumTypes(shiftId: number): void {
        this.hold(
            this.#agentService.apiAgentListIdtypeXGet({
                shiftId,
            }),
            this.#actions.requestMediumTypes,
        );
    }

    public requestBalance(): void {
        this.hold(this.#agentService.apiAgentGetBalanceGet(), this.#actions.requestBalance);
    }

    public requestSellerSettings(): void {
        this.hold(this.#agentService.apiAgentGetSellerSettingsGet(), this.#actions.requestSellerSettings);
    }

    public requestShiftReport(body: GetShiftReportRequest): void {
        this.hold(this.#agentService.apiAgentGetShiftReportPost({ body }), this.#actions.requestShiftReport);
    }

    public obsoleteToken(): void {
        this.#localStorageService.clear(StorageKeys.Access);
        this.#actions.setIsAuthorizesFlag(false);
    }

    private setDefaultState(): void {
        this.#state.set({
            loading: null,
            balance: null,
            errors: null,
            isAuthorized: !!this.authToken,
            user: null,
            shift: null,
            shiftReport: null,
            termCode: this.termCode,
        });
    }

    private connectSelectors(): void {
        this.#state.connect('mediumTypes', this.#actions.requestMediumTypes$);
        this.#state.connect('shiftReport', this.#actions.requestShiftReport$);
        this.#state.connect('sellerSettings', this.#actions.requestSellerSettings$);
        this.#state.connect('balance', this.#actions.requestBalance$);

        this.#state.connect(
            'shift',
            this.#actions.requestShift$.pipe(
                tap((shiftData: AddShiftResponse) => {
                    if (shiftData?.shiftId) {
                        this.#localStorageService.store(StorageKeys.TermCode, shiftData.termCode);
                        this.requestMediumTypes(shiftData?.shiftId);
                        this.redirectToMainPage();
                    }
                }),
            ),
        );

        this.#state.connect(
            'user',
            this.#actions.requestMe$.pipe(
                filter(Boolean),
                tap((loginData: LoginResponse) => {
                    this.#localStorageService.store(StorageKeys.Access, loginData.usrId);
                    this.#actions.setIsAuthorizesFlag(true);
                }),
            ),
        );

        this.#state.connect(
            'isAuthorized',
            this.#actions.setIsAuthorizesFlag$.pipe(
                tap((isAuthorized: boolean) => {
                    if (isAuthorized) {
                        this.requestShift();
                        this.requestBalance();
                        this.requestSellerSettings();
                        this.redirectToMainPage();
                    } else {
                        this.#clientState.resetClient();
                        this.redirectToSinIn();
                    }
                }),
            ),
        );
    }

    private redirectToMainPage(): void {
        this.#router.navigate([RouteEntity.Root, RouteEntity.Main]);
    }

    private redirectToSinIn(): void {
        this.#router.navigate([RouteEntity.Root, RouteEntity.SignIn]);
    }
}
